import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import MarkdownLite from "@/components/MarkdownLite";
import { Loader2, BookOpen, ArrowRight, FileDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SharedTeachShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/teach/${token}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090E] px-6 text-center">
        <div className="space-y-4 max-w-sm">
          <h1 className="text-2xl text-white">This draft isn't available</h1>
          <p className="text-sm text-slate-400">The link may have been removed — but the Word is still open to you.</p>
          <Link to="/login" className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#07090E] px-6 py-3 text-sm font-medium">
            Start your own study <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090E]">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090E]">
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <header className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF]">
            <img src="/openword-logo.png" alt="" className="w-5 h-5" /> OpenWord AI · Teaching draft
          </div>
          {data.hook && <p className="text-lg text-slate-400 leading-relaxed italic">{data.hook}</p>}
          <h1 className="text-3xl text-white leading-tight pt-1">{data.title}</h1>
          {data.passage_reference && <p className="text-sm text-slate-500">{data.passage_reference}</p>}
        </header>

        <article data-testid="shared-teach" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-5">
          {(data.blocks || []).map((b, i) => (
            <div key={i} className="space-y-1.5">
              {b.label && <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">{b.label}</div>}
              <div className="text-[15px] text-slate-300 leading-relaxed"><MarkdownLite text={b.body} /></div>
            </div>
          ))}
          <p className="text-[11px] text-slate-500 border-t border-[#1E273D] pt-3">{data.footer}</p>
        </article>

        <a data-testid="shared-teach-pdf" href={`${BACKEND}/api/teach/${token}/pdf`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 text-sm text-[#00E5FF] hover:underline">
          <FileDown className="w-4 h-4" strokeWidth={1.5} /> Download as PDF
        </a>

        <section className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 text-center space-y-3" data-testid="shared-teach-cta">
          <BookOpen className="w-6 h-6 mx-auto text-[#00E5FF]" strokeWidth={1.5} />
          <h2 className="text-2xl text-white">Study the passage for yourself</h2>
          <p className="text-sm text-slate-400 leading-relaxed max-w-md mx-auto">
            This draft was shaped from someone's own Bible study. OpenWord AI is a Scripture-first study
            companion — study a passage, then turn it into a devotional, teaching notes and more. Free to begin.
          </p>
          <Link to="/login" data-testid="shared-teach-signup"
                className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-7 py-3 text-sm font-medium transition-colors">
            Open the Word <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <p className="text-[11px] text-slate-500 pt-1">OpenWord AI is a study tool — the Word itself remains the belief system.</p>
        </section>
      </div>
    </div>
  );
}
